import { BadgeInfo } from '@/components/search-results/model';
import { Collection, Inclusion, PaymentPlan, PriceBreakUp, ProductApiData, UpfrontOffers, VariantConfiguration } from '@/services/catalogue/models/pdp';
import { TValue } from 'global';

export interface ProductPricings {
  getBasePrice: () => TValue;
  getProductPrice: () => TValue;
  getStrikePrice: () => TValue;
  getBaseDiscountRatio: () => TValue;
  getFinalPrice: () => TValue;
  getDiscountPercentage: () => TValue;
}

class BuyProductPricing implements ProductPricings {
  priceBreakUp: PriceBreakUp;

  constructor(priceBreakUp: PriceBreakUp) {
    this.priceBreakUp = priceBreakUp;
  }

  getStrikePrice(): TValue {
    return this.priceBreakUp?.strikePrice;
  }

  getBasePrice(): TValue {
    return this.priceBreakUp?.basePrice;
  }

  getBaseDiscountRatio(): TValue {
    return this.priceBreakUp?.baseDiscountPercentage;
  }

  getDiscountPercentage() {
    return this.priceBreakUp?.baseDiscountPercentage;
  };

  getFinalPrice(): TValue {
    return this.priceBreakUp?.basePrice;
  };

  getProductPrice(): TValue {
    return this.priceBreakUp?.postTax;
  }
}

class RentProductPricing implements ProductPricings {
  paymentPlan: PaymentPlan;

  constructor(paymentPlan: PaymentPlan) {
    this.paymentPlan = paymentPlan;
  }

  getStrikePrice(): TValue {
    return this.paymentPlan.strikePrice;
  }

  getBasePrice(): TValue {
    return this.paymentPlan.basePrice;
  }

  getBaseDiscountRatio(): TValue {
    return this.paymentPlan.productDiscountPercentage;
  }

  getDiscountPercentage() {
    return this.paymentPlan.totalDiscountPercentage;
  };

  getFinalPrice(): TValue {
    return this.paymentPlan.monthlyRental;
  }

  getProductPrice(): TValue {
    return this.paymentPlan.monthlyRental;
  }
}

export const getATCButtonLabel = (isAvailable: boolean, isAddedToCart: boolean) => {
  if (isAddedToCart) {
    return 'GO TO CART';
  } else if (isAvailable) {
    return 'ADD TO CART';
  }

  return 'OUT OF STOCK';
};

const getVariantConfigurationAsMap = (variantConfiguration: VariantConfiguration[]) => {
  return variantConfiguration?.reduce(
    (accumulator, currentValue) => {
      return {
        ...accumulator,
        [currentValue.value]: [currentValue.option.label],
      };
    },
    {}
  );
};

const getConfigValue = (variantConfigMap: any, label: string) => {
  if (variantConfigMap) {
    if (variantConfigMap.hasOwnProperty(label)) {
      return variantConfigMap[
        `${label}` as keyof typeof variantConfigMap
      ][0];
    } else return null;
  } else return null;
};

export const getProductPaymentPlanByTenure = (paymentPlans: PaymentPlan[], tenure: number): PaymentPlan | undefined => {
  if (!Array.isArray(paymentPlans) || paymentPlans.length == 0) {
    return;
  }

  const [plan] = paymentPlans?.filter(
    (plan) => {
      return plan.tenure === tenure;
    }
  );

  return plan;
};

export const getProductPricingByLop = ({ paymentPlan, priceBreakUp, lop }: { paymentPlan?: PaymentPlan, priceBreakUp: PriceBreakUp, lop: string; }): ProductPricings => {
  if (lop == 'RENT' && paymentPlan) {
    return new RentProductPricing(paymentPlan);
  } else {
    return new BuyProductPricing(priceBreakUp);
  }
};

const getTenureRelatedAttributes = (paymentPlans: PaymentPlan[], priceBreakUp: PriceBreakUp, selectedTenure: number, lop: string) => {
  const paymentPlan = getProductPaymentPlanByTenure(paymentPlans, selectedTenure);
  const productPricings = getProductPricingByLop({ paymentPlan, priceBreakUp, lop: lop });

  return {
    tenure_selected: selectedTenure,
    final_price: productPricings?.getFinalPrice().value,
    discount_ratio: productPricings?.getBaseDiscountRatio().value,
    strike_price: productPricings?.getStrikePrice().value,
  };
};

export const getPdpEventAttributes = ({ productDetails, selectedTenure }: {
  productDetails: {
    id?: number;
    badge?: BadgeInfo;
    collection?: Collection[];
    condition?: string;
    lineOfProduct?: string;
    inclusions?: Inclusion[];
    isAvailable?: boolean;
    availableUnits?: number;
    priceBreakUp?: PriceBreakUp;
    upfrontOffers?: UpfrontOffers;
    variantConfiguration?: VariantConfiguration[];
  }; selectedTenure?: number;
}) => {
  const { id, lineOfProduct: lop, isAvailable, priceBreakUp, collection, badge, availableUnits, variantConfiguration, upfrontOffers } = productDetails;
  let tenureRelatedAttributes = {};
  let variantConfigurationAttributes = {};

  const collectionString = collection
    ?.map((collection) => {
      return collection.label;
    })
    .join();

  if (Array.isArray(variantConfiguration)) {
    const variantConfigMap = getVariantConfigurationAsMap(variantConfiguration);

    variantConfigurationAttributes = {
      size: getConfigValue(variantConfigMap, 'size'),
      storage: getConfigValue(variantConfigMap, 'storage'),
      primary_material: getConfigValue(variantConfigMap, 'primary_material'),
      finish: getConfigValue(variantConfigMap, 'finish'),
      seating_capacity: getConfigValue(variantConfigMap, 'seating_capacity'),
      config_type: getConfigValue(variantConfigMap, 'config_type'),
      upholstery: getConfigValue(variantConfigMap, 'upholstery'),
      color: getConfigValue(variantConfigMap, 'color'),
    };
  }

  if (selectedTenure && upfrontOffers?.paymentPlans && priceBreakUp && lop) {
    tenureRelatedAttributes = getTenureRelatedAttributes(upfrontOffers?.paymentPlans ?? [], priceBreakUp!, selectedTenure, lop);
  }

  return {
    product_id: id,
    collections: collectionString ? collectionString : null,
    lineofproduct: lop,
    stock_flag: isAvailable ? 'true' : 'false',
    product_badge: badge?.label,
    stock_left_tag: availableUnits,
    ...variantConfigurationAttributes,
    ...tenureRelatedAttributes,
  };
};